import { useEffect, useState, useCallback, useMemo } from "react";
import { useMapsLibrary, useMap, Map, Marker } from "@vis.gl/react-google-maps";
import { isValidCoordinate } from "../utils/mapUtils";
import toast from "react-hot-toast";
import PropTypes from "prop-types";

const Directions = ({ origin, destination }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary("routes");
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [directionsError, setDirectionsError] = useState(null);

  // Debug logs
  console.log("Directions origin:", origin, "destination:", destination);

  // Memoize origin and destination to prevent unnecessary re-renders
  const memoizedOrigin = useMemo(() => origin, [origin?.lat, origin?.lng]);
  const memoizedDestination = useMemo(() => destination, [destination?.lat, destination?.lng]);

  // Initialize Directions Service and Renderer
  useEffect(() => {
    if (!routesLibrary || !map) return;

    const service = new routesLibrary.DirectionsService();
    const renderer = new routesLibrary.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#4285F4",
        strokeOpacity: 1,
        strokeWeight: 6,
      },
    });

    setDirectionsService(service);
    setDirectionsRenderer(renderer);
    setIsInitialized(true);

    return () => {
      renderer.setMap(null);
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [routesLibrary, map]);

  // Fetch Routes with prevention of multiple fetches
  const fetchRoutes = useCallback(() => {
    setDirectionsError(null);
    if (!directionsService || !isInitialized) return;
    if (!memoizedOrigin || !memoizedDestination) {
      toast.error("Missing origin or destination");
      setDirectionsError("Missing origin or destination");
      return;
    }
    if (!isValidCoordinate(memoizedOrigin)) {
      toast.error("Invalid origin coordinates");
      setDirectionsError("Invalid origin coordinates");
      return;
    }
    if (!isValidCoordinate(memoizedDestination)) {
      toast.error("Invalid destination coordinates");
      setDirectionsError("Invalid destination coordinates");
      return;
    }
    markers.forEach((marker) => marker.setMap(null));
    const geocoder = new window.google.maps.Geocoder();
    directionsService
      .route({
        origin: memoizedOrigin,
        destination: memoizedDestination,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      })
      .then((response) => {
        if (response.routes.length === 0) {
          setDirectionsError("No routes found");
          return;
        }
        directionsRenderer.setDirections(response);
        setRoutes(response.routes);
        const startLocation = response.routes[0].legs[0].start_location;
        const endLocation = response.routes[0].legs[0].end_location;
        const newMarkers = [
          new window.google.maps.Marker({
            position: startLocation,
            map,
            title: "Driver",
            icon: {
              url: "https://firebasestorage.googleapis.com/v0/b/eat-express-32d51.appspot.com/o/ASSETS%2Fdriver-marker.png?alt=media&token=2f502a6f-8594-46d7-9b1d-2d909a2970d1",
              scaledSize: new window.google.maps.Size(42, 42),
            },
          }),
          new window.google.maps.Marker({
            position: endLocation,
            map,
            title: "Destination",
            icon: {
              url: "https://firebasestorage.googleapis.com/v0/b/eat-express-32d51.appspot.com/o/ASSETS%2Fred-drop.png?alt=media&token=ff9a45c4-f9e9-4704-be05-e7f544793e3d",
              scaledSize: new window.google.maps.Size(42, 42),
            },
          }),
        ];

        // Add click event listeners to markers
        newMarkers.forEach((marker) => {
          const infoWindow = new window.google.maps.InfoWindow();
          marker.addListener("click", () => {
            geocoder.geocode(
              { location: marker.getPosition() },
              (results, status) => {
                if (status === "OK") {
                  if (results[0]) {
                    infoWindow.setContent(results[0].formatted_address);
                    infoWindow.open({ anchor: marker, map });
                  } else {
                    infoWindow.setContent("Address not found");
                    infoWindow.open({ anchor: marker, map });
                  }
                } else {
                  infoWindow.setContent("Unable to retrieve address");
                  infoWindow.open({ anchor: marker, map });
                }
              }
            );
          });
        });
        setMarkers(newMarkers);
      })
      .catch((error) => {
        console.error("Route fetching error:", error);
        toast.error("Could not calculate route");
        setDirectionsError("Could not calculate route");
      });
  }, [
    directionsService,
    memoizedOrigin,
    memoizedDestination,
    directionsRenderer,
    map,
    isInitialized,
  ]);

  // Trigger route fetching only when necessary
  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Render route details if available
  if (!routes.length) return null;

  return (
    <div className="directions">
      {/* Route details can be added here if needed */}
    </div>
  );
};

export default Directions;

Directions.propTypes = {
  origin: PropTypes.object.isRequired,
  destination: PropTypes.object.isRequired,
}; 